// Photo Compression Utility
// Compresses photos to save storage space while maintaining quality

const PhotoCompression = {

  // ========================================
  // COMPRESSION SETTINGS
  // ========================================

  settings: {
    compressed: {
      maxWidth: 1920,
      maxHeight: 1440,
      quality: 0.85,
      format: 'image/jpeg'
    },
    thumbnail: {
      maxWidth: 400,
      maxHeight: 300,
      quality: 0.7,
      format: 'image/jpeg'
    }
  },

  // ========================================
  // MAIN COMPRESSION FUNCTION
  // ========================================

  /**
   * Compress a photo file or blob
   * @param {File|Blob} file - The image file to compress
   * @param {string} type - 'compressed' or 'thumbnail'
   * @returns {Promise<Blob>} - Compressed image blob
   */
  async compressPhoto(file, type = 'compressed') {
    const config = this.settings[type];

    return new Promise((resolve, reject) => {
      // Validate input
      if (!file) {
        reject(new Error('No file provided'));
        return;
      }

      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        reject(new Error('File is not an image'));
        return;
      }

      const reader = new FileReader();

      reader.onerror = () => reject(new Error('Failed to read file'));

      reader.onload = (e) => {
        const img = new Image();

        img.onerror = () => reject(new Error('Failed to load image'));

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > config.maxWidth) {
                height = height * (config.maxWidth / width);
                width = config.maxWidth;
              }
            } else {
              if (height > config.maxHeight) {
                width = width * (config.maxHeight / height);
                height = config.maxHeight;
              }
            }

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw and compress
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create blob'));
                }
              },
              config.format,
              config.quality
            );
          } catch (error) {
            reject(error);
          }
        };

        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    });
  },

  /**
   * Create a thumbnail from a photo
   * @param {File|Blob} file - The image file
   * @returns {Promise<Blob>} - Thumbnail blob
   */
  async createThumbnail(file) {
    return this.compressPhoto(file, 'thumbnail');
  },

  /**
   * Compress to standard size (1920x1440)
   * @param {File|Blob} file - The image file
   * @returns {Promise<Blob>} - Compressed blob
   */
  async createCompressed(file) {
    return this.compressPhoto(file, 'compressed');
  },

  // ========================================
  // BLOB CONVERSION UTILITIES
  // ========================================

  /**
   * Convert blob to base64 data URL
   * @param {Blob} blob - Image blob
   * @returns {Promise<string>} - Base64 data URL
   */
  async blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
      reader.readAsDataURL(blob);
    });
  },

  /**
   * Convert base64 data URL to blob
   * @param {string} dataURL - Base64 data URL
   * @returns {Promise<Blob>} - Image blob
   */
  async dataURLToBlob(dataURL) {
    try {
      const response = await fetch(dataURL);
      return await response.blob();
    } catch (error) {
      throw new Error('Failed to convert data URL to blob');
    }
  },

  /**
   * Convert blob to object URL (for displaying images)
   * @param {Blob} blob - Image blob
   * @returns {string} - Object URL
   */
  blobToObjectURL(blob) {
    return URL.createObjectURL(blob);
  },

  /**
   * Revoke object URL (cleanup)
   * @param {string} objectURL - Object URL to revoke
   */
  revokeObjectURL(objectURL) {
    URL.revokeObjectURL(objectURL);
  },

  // ========================================
  // SIZE CALCULATION
  // ========================================

  /**
   * Calculate the size of a file/blob
   * @param {File|Blob} file - File or blob
   * @returns {Object} - Size in different units
   */
  getFileSize(file) {
    const bytes = file.size;
    return {
      bytes: bytes,
      kb: (bytes / 1024).toFixed(2),
      mb: (bytes / (1024 * 1024)).toFixed(2)
    };
  },

  /**
   * Calculate compression ratio
   * @param {number} originalSize - Original file size in bytes
   * @param {number} compressedSize - Compressed file size in bytes
   * @returns {Object} - Compression stats
   */
  getCompressionStats(originalSize, compressedSize) {
    const ratio = ((originalSize - compressedSize) / originalSize * 100).toFixed(2);
    const savedMB = ((originalSize - compressedSize) / (1024 * 1024)).toFixed(2);

    return {
      originalMB: (originalSize / (1024 * 1024)).toFixed(2),
      compressedMB: (compressedSize / (1024 * 1024)).toFixed(2),
      savedMB: savedMB,
      ratio: ratio + '%'
    };
  },

  // ========================================
  // BATCH COMPRESSION
  // ========================================

  /**
   * Compress multiple photos
   * @param {Array<File>} files - Array of image files
   * @param {Function} onProgress - Progress callback (current, total)
   * @returns {Promise<Array<{compressed: Blob, thumbnail: Blob, original: File}>>}
   */
  async compressMultiple(files, onProgress) {
    const results = [];
    let processed = 0;

    for (const file of files) {
      try {
        const compressed = await this.createCompressed(file);
        const thumbnail = await this.createThumbnail(file);

        results.push({
          original: file,
          compressed: compressed,
          thumbnail: thumbnail,
          stats: this.getCompressionStats(file.size, compressed.size)
        });

        processed++;
        if (onProgress) {
          onProgress(processed, files.length);
        }
      } catch (error) {
        console.error('Failed to compress file:', file.name, error);
        results.push({
          original: file,
          error: error.message
        });
      }
    }

    return results;
  },

  // ========================================
  // VALIDATION
  // ========================================

  /**
   * Check if file is a valid image
   * @param {File} file - File to validate
   * @returns {boolean} - True if valid image
   */
  isValidImage(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    return file && validTypes.includes(file.type);
  },

  /**
   * Check if file size is acceptable
   * @param {File} file - File to check
   * @param {number} maxSizeMB - Maximum size in MB (default: 10)
   * @returns {boolean} - True if size is acceptable
   */
  isAcceptableSize(file, maxSizeMB = 10) {
    const maxBytes = maxSizeMB * 1024 * 1024;
    return file && file.size <= maxBytes;
  },

  /**
   * Validate image file
   * @param {File} file - File to validate
   * @returns {Object} - Validation result
   */
  validateImage(file) {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    if (!this.isValidImage(file)) {
      return { valid: false, error: 'Invalid image type. Please use JPG, PNG, or WebP.' };
    }

    if (!this.isAcceptableSize(file, 20)) {
      return { valid: false, error: 'Image too large. Maximum size is 20 MB.' };
    }

    return { valid: true };
  }
};

export default PhotoCompression;
